import { Placement } from '@popperjs/core';
import { useClickOutside } from '../hooks/useClickOutside';
import React, { createContext, createRef, FC, useContext, useState } from 'react';
import { usePopper } from 'react-popper';
import { useDropdownKeys } from '../hooks/useDropdownKeys';
import styled from 'styled-components';
import { OffsetModifier } from '@popperjs/core/lib/modifiers/offset';
import { ReactPortal } from '@starryui10/layouts/ReactPortal';

interface IManagedDropdown {
    placement?: Placement;
    modifiers?: Partial<OffsetModifier>[];
    manager: (onClick: () => void, manager: IManagedDropdownManager) => React.ReactNode;
    width?: string;
    onClose?: () => void;
    disabled?: boolean;
}

interface IManagedDropdownManager {
    visible: boolean;
    disabled: boolean | undefined;
}

interface IManagedColumnProviderContext {
    closeDropdown: () => void;
}

const ManagedColumnProvider = createContext<IManagedColumnProviderContext>({
    closeDropdown: () => {
        /* nothing */
    },
});

export const useManagedDropdown = () => useContext(ManagedColumnProvider);

export const ManagedDropdown: FC<IManagedDropdown> = ({ modifiers, onClose, children, manager, placement, width = '220px', disabled }) => {
    const [referenceElement, setReferenceElement] = useState<HTMLDivElement>();
    const [popperElement, setPopperElement] = useState(null);
    const [visible, setVisible] = useState(false);

    const { wrapperRef } = useDropdownKeys(
        referenceElement,
        () => setVisible(true),
        (ref) => {
            setVisible(false);
            (ref as HTMLElement).focus();
        }
    );

    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement,
        modifiers,
    });

    const handleToggleVisible = () => {
        if (visible) {
            handleCloseDropdown();
        } else {
            setVisible(true);
        }
    };

    const handleCloseDropdown = () => {
        if (onClose) {
            onClose();
        }
        setVisible(false);
    };

    return (
        <>
            <div style={{ display: 'inline-block' }} ref={wrapperRef}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <div ref={setReferenceElement as any}>{manager(handleToggleVisible, { visible, disabled })}</div>
            </div>

            {visible && (
                <ReactPortal>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    <div ref={setPopperElement as any} style={styles.popper} {...attributes.popper}>
                        <DropdownWrapper onClose={handleCloseDropdown} width={width}>
                            {children}
                        </DropdownWrapper>
                    </div>
                </ReactPortal>
            )}
        </>
    );
};

const DropdownWrapper: FC<{ width: string; onClose: () => void }> = ({ children, width, onClose }) => {
    const outsideRef = createRef<HTMLDivElement>();

    useClickOutside(outsideRef, onClose);

    return (
        <div ref={outsideRef}>
            <ManagedColumnProvider.Provider
                value={{
                    closeDropdown: onClose,
                }}
            >
                <Dropdown width={width}>{children}</Dropdown>
            </ManagedColumnProvider.Provider>
        </div>
    );
};

const Dropdown = styled.div<{ width: string }>`
    background: white;
    padding: 0;
    margin: 0;
    border: 1px solid #dae5ec;
    box-shadow: 0px 2px 30px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    width: ${(props) => props.width};
    z-index: 2;
`;
